import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { SlideOverShell } from '../../../ui/slideOverParts';
import { slideOverCancelClass, slideOverPrimaryClass } from '../../../ui/InventorySlideOver';
import { TimeControls } from '../../../ui/TimeControls';
import { addPeriod, calculateEndDate, normalizeDate, startOfPeriod } from '../../../../utils/dateUtils';
import {
  createReportJob,
  CreateReportJobParams,
  downloadReportJobFile,
  getReportJobStatus,
  ReportJobExpiredError,
  ReportJobLimitError,
} from '../../../../services/analysis/analysisReportsService';
import { lightAmberBG, lightBlueBG, lightRedBG, mutedText, primaryText, redTextColor, secondaryText } from '../../../../styling/stylingHelper';
import { ReportTypeOption } from './reportTypes';
import InventoryCountPicker, { InventoryCountSelection } from './InventoryCountPicker';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_FAILURES = 3;

type Phase = 'idle' | 'queued' | 'running' | 'downloading' | 'done' | 'error';

interface Props {
  restaurantId: string;
  option: ReportTypeOption;
  onClose: () => void;
}

/** The last completed week / month — a report on the running period is rarely what's wanted. */
const defaultPeriodStart = (view: 'week' | 'month') => startOfPeriod(addPeriod(normalizeDate(new Date()), view, -1), view);

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser time to begin reading the blob before releasing it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ReportDownloadSlideOver({ restaurantId, option, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const periodView = option.picker === 'month' ? 'month' : 'week';
  const [periodStart, setPeriodStart] = useState<Date>(() => defaultPeriodStart(periodView));
  const [inventory, setInventory] = useState<InventoryCountSelection>({ groupId: null, session: null, baseline: null });
  const [phase, setPhase] = useState<Phase>('idle');
  const [position, setPosition] = useState(0);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<{ cancelled: boolean } | null>(null);

  // Closing the panel abandons the job; the server drops it after its TTL.
  useEffect(() => () => { if (pollRef.current) pollRef.current.cancelled = true; }, []);

  const busy = phase === 'queued' || phase === 'running' || phase === 'downloading';
  const isPeriod = option.picker === 'week' || option.picker === 'month';
  const ready = isPeriod || (option.picker === 'inventoryCount' ? !!inventory.session : !!inventory.session && !!inventory.baseline);

  const buildParams = (): CreateReportJobParams => {
    const base: CreateReportJobParams = {
      restaurantId,
      reportType: option.type,
      language: i18n.language.startsWith('nl') ? 'nl' : 'en',
    };
    if (isPeriod) {
      return { ...base, startDate: format(periodStart, 'yyyy-MM-dd'), endDate: format(calculateEndDate(periodStart, periodView), 'yyyy-MM-dd') };
    }
    if (option.picker === 'inventoryCount') {
      return { ...base, groupId: inventory.groupId!, countSessionId: inventory.session!.sessionId! };
    }
    // Leakage: the server finds the closing session by any of its count ids.
    return { ...base, groupId: inventory.groupId!, endingCountId: inventory.session!.countIds[0], baselineSessionId: inventory.baseline!.sessionId! };
  };

  const handleDownload = async () => {
    if (!ready || busy) return;
    if (pollRef.current) pollRef.current.cancelled = true;
    const ticket = { cancelled: false };
    pollRef.current = ticket;
    setError(null);
    setWarnings([]);
    setPhase('queued');

    try {
      const { jobId, position: initialPosition } = await createReportJob(buildParams());
      if (ticket.cancelled) return;
      setPosition(initialPosition);

      let failures = 0;
      while (!ticket.cancelled) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (ticket.cancelled) return;
        let state;
        try {
          state = await getReportJobStatus(jobId);
          failures = 0;
        } catch (e) {
          // A blip is retried; an expired job or a dead server is not.
          if (e instanceof ReportJobExpiredError || ++failures >= POLL_MAX_FAILURES) throw e;
          continue;
        }
        if (ticket.cancelled) return;
        if (state.warnings) setWarnings(state.warnings);
        if (state.status === 'failed') throw new Error(state.error || t('analysisReports.errors.failed'));
        if (state.status === 'queued' || state.status === 'running') {
          setPhase(state.status);
          setPosition(state.position);
          continue;
        }
        setPhase('downloading');
        const blob = await downloadReportJobFile(jobId);
        if (ticket.cancelled) return;
        triggerDownload(blob, state.filename || `${option.type}.pdf`);
        setPhase('done');
        return;
      }
    } catch (e: any) {
      if (ticket.cancelled) return;
      console.error('Failed to generate report:', e);
      // Precondition errors (e.g. no saved count) arrive as a failed job's `error`, shown as-is.
      setError(
        e instanceof ReportJobExpiredError ? t('analysisReports.errors.expired')
        : e instanceof ReportJobLimitError ? t('analysisReports.errors.tooMany')
        : e?.message || t('analysisReports.errors.failed')
      );
      setPhase('error');
    }
  };

  const statusText =
    phase === 'queued' ? t('analysisReports.status.queued', { position: Math.max(position, 1) })
    : phase === 'running' ? t('analysisReports.status.running')
    : phase === 'downloading' ? t('analysisReports.status.downloading')
    : phase === 'done' ? t('analysisReports.status.done')
    : null;

  const Icon = option.icon;

  return (
    <SlideOverShell onClose={onClose} closeLabel={t('common.close')}>
      <div className="flex items-start gap-3 mb-2">
        <Icon className={cn('w-6 h-6 mt-1 shrink-0', secondaryText)} />
        <h2 className={cn('text-2xl sm:text-[28px] leading-9 font-semibold break-words', primaryText)}>
          {t(`analysisReports.types.${option.type}.title`)}
        </h2>
      </div>
      <p className={cn('mb-6 text-sm sm:text-base', mutedText)}>{t(`analysisReports.types.${option.type}.description`)}</p>

      {isPeriod && (
        <div className="mb-6">
          <p className={cn('mb-2 text-sm font-semibold', secondaryText)}>
            {t(option.picker === 'month' ? 'analysisReports.picker.month' : 'analysisReports.picker.week')}
          </p>
          <TimeControls
            startingDate={periodStart}
            selectedTimeOption={periodView}
            onChange={(start) => { if (!busy) setPeriodStart(normalizeDate(start)); }}
            align="left"
          />
        </div>
      )}

      {option.type === 'inventoryOrder' && (
        <div className={cn('mb-5 flex gap-2 rounded-xl px-3 py-2.5 text-sm', lightBlueBG, secondaryText)}>
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t('analysisReports.picker.orderListNote')}</span>
        </div>
      )}
      {option.type === 'inventoryManagement' && (
        <p className={cn('mb-5 text-sm', mutedText)}>{t('analysisReports.picker.countReviewNote')}</p>
      )}

      {!isPeriod && (
        <div className={cn('mb-6', busy && 'pointer-events-none opacity-60')}>
          <InventoryCountPicker
            restaurantId={restaurantId}
            mode={option.picker === 'inventoryPair' ? 'pair' : 'single'}
            value={inventory}
            onChange={setInventory}
          />
        </div>
      )}

      {warnings.length > 0 && (
        <div className={cn('mb-4 flex gap-2 rounded-xl px-3 py-2.5 text-sm', lightAmberBG, secondaryText)}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="flex flex-col gap-1 min-w-0">
            <span className="font-semibold">{t('analysisReports.warningsTitle')}</span>
            {warnings.map((w) => <span key={w} className="break-words">{w}</span>)}
          </div>
        </div>
      )}

      {error && (
        <div className={cn('mb-4 rounded-xl px-3 py-2.5 text-sm break-words', lightRedBG, redTextColor)}>{error}</div>
      )}

      {statusText && (
        <div className={cn('mb-4 flex items-center gap-2 text-sm font-medium', secondaryText)}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          <span>{statusText}</span>
        </div>
      )}

      <div className="mt-auto flex gap-3 sm:gap-6 pt-4">
        <button type="button" onClick={onClose} className={slideOverCancelClass}>
          {t('common.close')}
        </button>
        <button type="button" onClick={handleDownload} disabled={!ready || busy} className={slideOverPrimaryClass}>
          {busy ? t('analysisReports.generating') : t('analysisReports.download')}
        </button>
      </div>
    </SlideOverShell>
  );
}
